import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import Video from '@atoms/Video/Video';
import { AspectRatioType } from '@models/IAspectRatio';
import { IImage } from '@models/IImage';
import { IMeta } from '@models/IMeta';
import { IVideo } from '@models/IVideo';
import { IVideoSubtitle } from '@models/IVideoSubtitle';
import updateDataLayer from '@utils/helpers/gtm';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import classNames from 'classnames';
import { AnimatePresence, MotionValue, motion, useInView } from 'framer-motion';
import { RefObject, useEffect, useRef, useState } from 'react';

interface Props {
  readonly autoPlay?: boolean;
  readonly controls?: boolean;
  readonly coverImage?: IImage;
  readonly dataTestId?: string;
  readonly epiEdit?: string;
  readonly hasInVideoPlayButton?: boolean;
  readonly hasInVideoMuteButton?: boolean;
  readonly hasVideoOverlay?: boolean;
  readonly inVideoMuteButtonPositionClassName?: string;
  readonly inVideoMuteButtonTabIndex?: number;
  readonly inVideoPlayButtonClassName?: string;
  readonly inVideoPlayButtonPositionClassName?: string;
  readonly inVideoPlayButtonTabIndex?: number;
  readonly inViewThreshold?: number;
  readonly meta?: IMeta;
  readonly playOnCoverClick?: boolean;
  readonly subtitles?: IVideoSubtitle[];
  readonly video: IVideo;
  readonly videoClasses?: string;
  readonly viedeoInlineStyles?: React.CSSProperties;
  readonly videoRatio?: AspectRatioType;
  readonly videoWidthStyle?: string;
  readonly videoWrapperOpacityProgress?: MotionValue<number>;
  readonly wrapperClasses?: string;
  readonly onFocusCallback?: (e: React.FocusEvent<HTMLButtonElement>) => void;
  readonly onVideoReady?: () => void;
}

const InViewVideo = ({
  autoPlay = false,
  controls = true,
  coverImage,
  dataTestId = 'In-View-Video',
  epiEdit,
  hasInVideoPlayButton,
  hasInVideoMuteButton,
  hasVideoOverlay = true,
  inVideoMuteButtonPositionClassName = 'bottom-5 left-15',
  inVideoMuteButtonTabIndex = 0,
  inVideoPlayButtonClassName,
  inVideoPlayButtonPositionClassName = 'bottom-5 left-5',
  inVideoPlayButtonTabIndex = 0,
  inViewThreshold = 0.5,
  meta,
  playOnCoverClick = true,
  subtitles,
  video,
  videoClasses,
  viedeoInlineStyles,
  videoRatio,
  videoWidthStyle,
  videoWrapperOpacityProgress,
  wrapperClasses,
  onFocusCallback,
  onVideoReady,
}: Props) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoManuallyPaused, setIsVideoManuallyPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(hasInVideoMuteButton);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLDivElement>(null);
  const inView = useInView(videoRef, { amount: inViewThreshold });
  const isAutoPlaying = !hasInVideoPlayButton
    ? autoPlay
    : autoPlay && !isVideoManuallyPaused;
  const {
    playVideoAriaLabel,
    pauseVideoAriaLabel,
    muteVideoAriaLabel,
    unmuteVideoAriaLabel,
  } = useAriaLabels();
  const isVideoInteractionTrackingIgnored = useRef(false);

  useEffect(() => {
    setIsPlaying(isAutoPlaying && inView);
    isVideoInteractionTrackingIgnored.current = true;

    // When we have a custom mute/unmute button, we don't want it to be affected by the play/pause button
    if (!hasInVideoMuteButton)
      setIsMuted(!controls || !inView || isAutoPlaying);
  }, [isAutoPlaying, inView, controls, hasInVideoMuteButton]);

  // We trigger the videoInteraction event in two ways:
  // First scenario, if we have out custom play/pause button in place,
  // we trigger the event on clicking that button
  // Second scenario, if we use the native controls, we use a flag to detect
  // when the video is autoplaying/autopausing and we trigger the events from
  // the video play/pause callbacks
  const handleVideoInteractionEvent = (interaction: string) => {
    updateDataLayer({
      event: 'videoInteraction',
      video_interaction: interaction,
      video_title: video?.url,
    });
  };

  const handleInVideoPlayButtonInteraction = () => {
    setIsPlaying(!isPlaying);
    setIsVideoManuallyPaused(isPlaying);
    handleVideoInteractionEvent(isPlaying ? 'pause' : 'play');
  };

  const handleVideoStateChange = (newIsPlaying: boolean) => {
    setIsPlaying(newIsPlaying);

    if (controls && !isVideoInteractionTrackingIgnored.current) {
      handleVideoInteractionEvent(newIsPlaying ? 'play' : 'pause');
    }
    isVideoInteractionTrackingIgnored.current = false;
  };

  return (
    <div
      className={classNames('relative', wrapperClasses)}
      data-testid={dataTestId}
      data-epi-property-name={epiEdit}
    >
      <Video
        classes={videoClasses}
        inlineStyles={viedeoInlineStyles}
        title={video?.url}
        videoSource={video?.url}
        ref={videoRef as unknown as RefObject<HTMLDivElement>}
        videoAttributes={{
          playsinline: true,
          muted: isMuted,
          playing: isPlaying,
          loop: true,
        }}
        onVideoReady={() => {
          setVideoReady(true);
          onVideoReady?.();
        }}
        epiEdit={meta?.editHints?.video as string | undefined}
        controls={controls}
        videoRatio={videoRatio}
        subtitles={subtitles}
        isPlaying={isPlaying}
        onVideoPause={() => {
          handleVideoStateChange(false);
        }}
        onVideoPlay={() => {
          handleVideoStateChange(true);
        }}
        videoWrapperOpacityProgress={videoWrapperOpacityProgress}
        videoWidthStyle={videoWidthStyle}
        onVolumeChange={(videoElement) => {
          setIsMuted(videoElement.volume === 0 || videoElement.muted);
        }}
      />

      {hasInVideoPlayButton && videoReady && (
        <button
          type="button"
          aria-label={isPlaying ? pauseVideoAriaLabel : playVideoAriaLabel}
          className={classNames(
            'absolute z-20 flex h-7.5 w-7.5 items-center justify-center rounded-full bg-black bg-opacity-50',
            inVideoPlayButtonPositionClassName,
            inVideoPlayButtonClassName
          )}
          onClick={() => {
            handleInVideoPlayButtonInteraction();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleInVideoPlayButtonInteraction();
            }
          }}
          onFocus={onFocusCallback}
          data-testid={`${dataTestId}-${isPlaying ? 'Pause' : 'Play'}-Button`}
          data-video-control="toggle"
          tabIndex={inVideoPlayButtonTabIndex}
        >
          <Icon
            name={isPlaying ? 'pause' : 'play_icon_filled'}
            colorClassName="text-white"
            size="1.4rem"
            className="pointer-events-none"
          />
        </button>
      )}

      {hasInVideoMuteButton && videoReady && (
        <button
          type="button"
          aria-label={isMuted ? unmuteVideoAriaLabel : muteVideoAriaLabel}
          className={classNames(
            'absolute z-20 flex h-7.5 w-7.5 items-center justify-center rounded-full bg-black bg-opacity-50',
            inVideoMuteButtonPositionClassName
          )}
          onClick={() => {
            setIsMuted(!isMuted);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsMuted(!isMuted);
            }
          }}
          data-testid={`${dataTestId}-${isMuted ? 'Unmute' : 'Mute'}-Button`}
          data-video-control="toggle"
          tabIndex={inVideoMuteButtonTabIndex}
        >
          <Icon
            name={isMuted ? 'mute' : 'volume_up'}
            colorClassName="text-white"
            size="1.4rem"
            className="pointer-events-none"
          />
        </button>
      )}

      {(coverImage || !isAutoPlaying) && hasVideoOverlay && (
        <AnimatePresence>
          {!isPlaying && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                if (playOnCoverClick) {
                  setIsPlaying(true);
                  handleVideoInteractionEvent('play');
                  isVideoInteractionTrackingIgnored.current = true;
                }
              }}
              className="group absolute inset-0 flex items-center justify-center hover:cursor-pointer"
            >
              {coverImage && (
                <Image
                  classes="relative w-full h-full"
                  image={coverImage}
                  imageClasses="w-full h-full object-cover object-center"
                  epiEdit={meta?.editHints?.video as string | undefined}
                  dataTestId={`${dataTestId}-Cover-Image`}
                />
              )}

              {!isAutoPlaying && coverImage && (
                <button
                  type="button"
                  aria-label={playVideoAriaLabel}
                  className="absolute bottom-2.5 left-2.5 flex items-center md:bottom-5 md:left-5"
                  onClick={() => {
                    setIsPlaying(true);
                    handleVideoInteractionEvent('play');
                    isVideoInteractionTrackingIgnored.current = true;
                  }}
                  data-testid={`${dataTestId}-Play-Button`}
                >
                  <div className="h-[2.1875rem] w-[2.1875rem] md:h-[2.875rem] md:w-[2.875rem]">
                    <Icon name="play_icon_circle" size="100%" />
                  </div>
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default InViewVideo;
